import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppSelector } from 'app/config/store';
import { colors } from 'app/shared/theme/colors';
import { useTranslation } from 'app/shared/i18n';
import axios from 'axios';

export const RankPreview = () => {
  const { t } = useTranslation();
  const [ranks, setRanks] = useState<any[]>([]);
  const isAuthenticated = useAppSelector(state => state.authentication.isAuthenticated);
  const currentRank = useAppSelector(state => state.authentication.account?.rank);

  useEffect(() => {
    axios
      .get('/api/ranks')
      .then(res => {
        const sorted = (res.data || []).sort((a, b) => (a.minAmount || 0) - (b.minAmount || 0));
        setRanks(sorted);
      })
      .catch(() => {
        /* ignore */
      });
  }, []);

  if (ranks.length === 0) return null;

  const maxDiscount = Math.max(...ranks.map(r => r.discountPercent || 0));
  const nextRank = (() => {
    if (!currentRank) return ranks[0];
    const idx = ranks.findIndex(r => r.id === currentRank.id);
    return idx >= 0 && idx < ranks.length - 1 ? ranks[idx + 1] : null;
  })();

  return (
    <div style={{ padding: '10px 24px', borderBottom: `1px solid ${colors.border}` }}>
      <style>{`
        @keyframes rankGlow {
          0%, 100% { box-shadow: 0 0 8px rgba(34,197,94,0.2), 0 0 0 0 rgba(34,197,94,0.1); }
          50% { box-shadow: 0 0 16px rgba(34,197,94,0.35), 0 0 0 4px rgba(34,197,94,0.08); }
        }
        @keyframes rankShine {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
      `}</style>
      <div
        style={{
          maxWidth: 1280,
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        {isAuthenticated ? (
          <>
            <span style={{ fontSize: 12, color: colors.dim }}>{`👑 ${t('rank.memberRank')}`}</span>

            {/* Current rank badge with glow + shine */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 14px',
                borderRadius: 8,
                fontSize: 13,
                background: 'linear-gradient(135deg, rgba(34,197,94,0.12), rgba(22,163,74,0.08))',
                border: '1.5px solid rgba(34,197,94,0.4)',
                color: colors.accent,
                fontWeight: 700,
                animation: 'rankGlow 2.5s ease-in-out infinite',
              }}
            >
              <span style={{ fontSize: 14 }}>{currentRank ? '⭐' : '👤'}</span>
              <span
                style={{
                  background: 'linear-gradient(90deg, #22c55e, #86efac, #22c55e)',
                  backgroundSize: '200% auto',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  animation: 'rankShine 3s linear infinite',
                  fontWeight: 800,
                }}
              >
                {currentRank?.name || 'Member'}
              </span>
              {currentRank?.discountPercent > 0 && (
                <span
                  style={{
                    padding: '1px 6px',
                    borderRadius: 4,
                    fontSize: 10,
                    fontWeight: 700,
                    background: 'rgba(16,185,129,0.15)',
                    color: colors.success,
                    fontFamily: "'JetBrains Mono', monospace",
                  }}
                >
                  -{currentRank.discountPercent}%
                </span>
              )}
            </div>

            {/* Next rank hint */}
            {nextRank && (
              <>
                <span style={{ color: colors.dim, fontSize: 12 }}>→</span>
                <span style={{ fontSize: 12, color: colors.muted }}>
                  {t('rank.upgradeToGet')} <strong style={{ color: colors.text }}>{nextRank.name}</strong> {t('rank.toGetDiscount')}{' '}
                  <strong style={{ color: colors.success, fontFamily: "'JetBrains Mono', monospace" }}>-{nextRank.discountPercent}%</strong>
                </span>
              </>
            )}

            <Link to="/membership" style={{ fontSize: 12, fontWeight: 600, color: colors.accent, marginLeft: 4 }}>
              {t('rank.viewAll')}
            </Link>
          </>
        ) : (
          <>
            <span style={{ fontSize: 13 }}>👑</span>
            <span style={{ fontSize: 13, color: colors.muted }}>
              {t('rank.maxDiscount')}{' '}
              <strong style={{ color: colors.accent, fontFamily: "'JetBrains Mono', monospace" }}>{maxDiscount}%</strong>{' '}
              {t('rank.discountForMembers')}
            </span>
            <span style={{ color: colors.dim, margin: '0 2px' }}>—</span>
            <Link to="/account/register" style={{ fontSize: 13, fontWeight: 600, color: colors.accent }}>
              {t('rank.register')}
            </Link>
            <span style={{ color: colors.dim }}>&</span>
            <Link to="/membership" style={{ fontSize: 13, fontWeight: 600, color: colors.accent }}>
              {t('rank.depositNowLink')}
            </Link>
            <span style={{ fontSize: 13, color: colors.muted }}>{t('rank.toEnjoyOffers')}</span>
          </>
        )}
      </div>
    </div>
  );
};
